import { useCallback, useState } from 'react';
import ReactNativeBlobUtil from 'react-native-blob-util';
import Modal from 'utils/packages/Modal';
import { getActivities, getScannedSignatureUploadUrl, createProductTransfer as createProductTransferAction, } from 'actions/invest';
import { Amplitude } from 'utils';
import { muteAllButThrows, useFetchOld } from 'hooks';
import ImageResizer from 'utils/packages/imageResizer';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectActiveTradingAccountId, selectOnboardingTradingAccountForOnboardingFunnel } from '../reducers/selectors';
import { amplitudeSource } from '../utils/analytics';
export const MAX_UPLOAD_MB = 2 * 0.9; // 2MB reduced by 10% as a safety margin
export const resizeFileForced = async (file, sizeLimit) => {
    let currentFile = file;
    let currentSize = (await ReactNativeBlobUtil.fs.stat(file.path.replace('file://', ''))).size;
    do {
        const coefficient = Math.min(0.9, 1 / Math.sqrt(currentSize / (sizeLimit * 0.9))); // always shrink - 0.9
        // eslint-disable-next-line no-await-in-loop
        const newFile = await ImageResizer.createResizedImage(currentFile.path, Math.round(coefficient * currentFile.width), Math.round(coefficient * currentFile.height), 'JPEG', 90 // between pixel_count vs. jpeg_quality, pixel_count for open_cv is the priority
        );
        if (currentFile.path !== file.path) {
            // remove intermediate images
            // eslint-disable-next-line no-await-in-loop
            await ReactNativeBlobUtil.fs.unlink(currentFile.path.replace('file://', '')).catch(() => { });
        }
        currentFile = newFile;
        // eslint-disable-next-line no-await-in-loop
        currentSize = (await ReactNativeBlobUtil.fs.stat(newFile.path.replace('file://', ''))).size;
    } while (currentSize > sizeLimit);
    return currentFile;
};
export function useCreateProductTransfer(accountId) {
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const [fetching, setFetching] = useState(false);
    const [, , , createTransfer] = useFetchOld(muteAllButThrows);
    const [, , , generateScannedSignatureUploadUrl] = useFetchOld(muteAllButThrows);
    const activeTradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const onboardingTradingAccount = useAppSelector(selectOnboardingTradingAccountForOnboardingFunnel);
    const tradingAccountId = accountId || activeTradingAccountId || onboardingTradingAccount?.account.id;
    const createProductTransfer = useCallback(async (params) => {
        if (tradingAccountId) {
            setFetching(true);
            try {
                const uploadUrlResponse = params.uploadInfo
                    ? await generateScannedSignatureUploadUrl(getScannedSignatureUploadUrl())
                    : undefined;
                if (params.uploadInfo && !uploadUrlResponse) {
                    return; // handled automatically
                }
                if (params.uploadInfo && uploadUrlResponse) {
                    const { uploadUrl } = uploadUrlResponse;
                    const finalFile = await resizeFileForced(params.uploadInfo, MAX_UPLOAD_MB * 1024 * 1024);
                    try {
                        const response = await ReactNativeBlobUtil.fetch('PUT', uploadUrl, { 'Content-Type': 'image/jpeg' }, ReactNativeBlobUtil.wrap(finalFile.path.replace('file://', '')));
                        const { status } = response.info();
                        if (status < 200 || status >= 300) {
                            throw new Error('Upload failed');
                        }
                        if (finalFile.path !== params.uploadInfo.path) {
                            ReactNativeBlobUtil.fs.unlink(finalFile.path.replace('file://', '')).catch(() => { });
                        }
                    }
                    catch {
                        Modal.showError('Upload failed');
                        return;
                    }
                }
                const result = await createTransfer(createProductTransferAction({
                    accountId: tradingAccountId,
                    transferProviderId: params.provider.id,
                    transferType: params.type,
                    previousProviderAccountType: params.previousProviderAccountType,
                    transferValue: params.balance,
                    signatureSvg: params.signatureSvg,
                    transferTaxYear: params.transferTaxYear,
                    scannedSignature: uploadUrlResponse?.uuid,
                    ...(params.earlyClosureWaiver && {
                        earlyClosureWaiver: params.earlyClosureWaiver,
                    }),
                    ...('accountReference' in params
                        ? { previousProviderAccountReference: params.accountReference }
                        : {
                            previousProviderSortCode: params.previousProviderSortCode,
                            previousProviderAccountNumber: params.previousProviderAccountNumber,
                        }),
                }));
                if (!result)
                    return; // handled automatically
                Amplitude.logEvent('Submitted Request', {
                    source: amplitudeSource,
                    account: accountId ? 'jisa' : 'isa',
                    type: accountId ? 'jisa_transfer' : 'isa_transfer',
                });
                const { productTransferId } = result;
                if (!accountId && activeTradingAccountId) {
                    dispatch(getActivities({ accountId: tradingAccountId, paging: { page: 1, perPage: 30 } }));
                    navigation.popTo('InvestPortfolio'); // should exist in nav stack
                    navigation.navigate('ProductTransferProgress', {
                        productTransferId,
                    });
                }
                else {
                    navigation.popToTop();
                }
            }
            catch {
                // handled automatically
            }
            finally {
                setFetching(false);
            }
        }
    }, [accountId, activeTradingAccountId, createTransfer, generateScannedSignatureUploadUrl, tradingAccountId]);
    return [fetching, createProductTransfer];
}
